import React, { useRef, ElementType, CSSProperties, useContext } from "react";
import { useDrag, useDrop } from "react-dnd";
import { makePostCall } from "../effects/requests";
import { endPoints } from "../utils/endpoints";
import { emptyFunction, getCompanyName } from "../utils";
import { MyContext } from "../context";

const ITEM_TYPE = "COMMON_DRAGGABLE";

interface DraggableItemProps {
    index: number;
    moveItem: (dragIndex: number, hoverIndex: number) => void;
    children: React.ReactNode;
    className?: string;
    onClick?: () => void;
    containerTag?: ElementType;
    style?: CSSProperties;
    itemId?: any;
    parentId?: any;
    data?: any;
    renderType: 'vault' | 'folder' | 'image'
}

const DraggableItem: React.FC<DraggableItemProps> = ({
    index,
    moveItem,
    children,
    className,
    onClick,
    containerTag: Container = "div",
    style,
    itemId,
    renderType = 'vault',
    parentId,
    data
}) => {

    const { userData } = useContext(MyContext)

    const ref = useRef<HTMLDivElement>(null);

    const [{ isDragging }, drag] = useDrag({
        type: ITEM_TYPE,
        item: { index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const reorder = (oldOrder: number, newOrder: number) => {
        const payload: any = {
            type: renderType,
            production_company_name: getCompanyName(userData),
            reorder_data: [
                {
                    id: itemId,
                    old_order: oldOrder,
                    new_order: newOrder,
                },
            ],
        }

        if (renderType === 'image') {
            payload.folder_id = parentId
        } else if (renderType === 'folder') {
            payload.location_id = parentId
        }

        makePostCall(endPoints.LOCATION_REORDER, payload, false, true).then(
            emptyFunction,
            emptyFunction
        )
    }

    const [, drop] = useDrop({
        accept: ITEM_TYPE,

        hover: (item: { index: number; originalIndex?: number }) => {
            if (!ref.current) return

            if (item.index === index) return

            // Save the initial position once
            if (item.originalIndex === undefined) {
                item.originalIndex = item.index
            }

            moveItem(item.index, index)
            item.index = index
        },

        drop: (item: { index: number; originalIndex?: number }) => {
            const oldOrder = item.originalIndex ?? item.index

            setTimeout(() => {
                reorder(oldOrder + 1, index + 1)
            }, 800);
        },
    })

    drag(drop(ref));

    return (
        <Container
            ref={ref}
            className={className}
            style={{
                opacity: isDragging ? 0.5 : 1,
                // cursor: "move",
                ...style
            }}
            onClick={onClick}
        >
            {children}
        </Container>
    );
};

export default DraggableItem;